import { useState, useEffect, useCallback } from 'react';
import { getAttachmentSignedUrl } from '../firebase/storage.service';
import { attachmentPreviewCache } from '../utils/attachmentPreviewCache';

export function useAttachmentPreview(storagePath) {
    const [previewData, setPreviewData] = useState(() => {
        const cached = attachmentPreviewCache.get(storagePath);
        return {
            url: cached?.url || null,
            error: cached?.error || null,
            loading: attachmentPreviewCache.isLoading(storagePath),
        };
    });

    useEffect(() => {
        if (!storagePath) return;

        // Subscribe to cache changes
        const unsubscribe = attachmentPreviewCache.subscribe(storagePath, (entry) => {
            if (entry) {
                setPreviewData({
                    url: entry.url,
                    error: entry.error || null,
                    loading: false,
                });
            }
        });

        // If not cached and not loading, start loading
        if (!attachmentPreviewCache.get(storagePath) && !attachmentPreviewCache.isLoading(storagePath)) {
            loadPreview();
        }

        return unsubscribe;
    }, [storagePath]);

    const loadPreview = useCallback(async () => {
        if (!storagePath || attachmentPreviewCache.isLoading(storagePath)) return;

        attachmentPreviewCache.setLoading(storagePath, true);
        setPreviewData(prev => ({ ...prev, loading: true }));

        try {
            const url = await getAttachmentSignedUrl(storagePath);
            const entry = { url, timestamp: Date.now() };
            attachmentPreviewCache.set(storagePath, entry);
        } catch (error) {
            console.error('Failed to load attachment preview:', storagePath, error);
            const entry = { url: '', timestamp: Date.now(), error: error.message };
            attachmentPreviewCache.set(storagePath, entry);
        } finally {
            attachmentPreviewCache.setLoading(storagePath, false);
        }
    }, [storagePath]);

    return previewData;
}